/**
 * Merging the server's targeting data into the Target Data form.
 *
 * This is the exact spot the page's original bug lived. It used to be:
 *
 *     if (targetData) { setFormData(prev => ({ ...prev, ...targetData })); }
 *
 * with nothing on the else, against an endpoint that did not exist - so the fetch failed, the merge
 * never ran, and the component's hardcoded initial state rendered as though it were the customer's
 * data on every slot of every account. The page now refuses to render a form at all without data;
 * this function is the other half, and it is a pure function so it can be tested.
 *
 * Two rules, both learned from that failure:
 *
 *   1. Only keys the FORM already declares are taken. The response carries `slot_name` alongside
 *      the 50 targeting fields, and the form posts itself back wholesale - so a blind spread puts
 *      `slot_name` in the body and the server (which reports unknown fields rather than dropping
 *      them) refuses the entire save. Anything the response gains later is ignored the same way.
 *   2. A null from the server becomes the empty form value for its type, never the form's DEFAULT.
 *      Falling back to a default is how a blank field silently becomes "HASHTAGS" or "5" - a value
 *      the operator never chose and cannot tell apart from one they did.
 */
export function mergeTargetData(form, response) {
  if (!form) return form;
  if (!response || typeof response !== 'object') return form;

  const next = { ...form };
  let changed = false;

  Object.keys(form).forEach((key) => {
    if (!Object.prototype.hasOwnProperty.call(response, key)) return;
    const incoming = response[key];
    const value = incoming === null || incoming === undefined
      ? (typeof form[key] === 'boolean' ? false : '')
      : incoming;
    if (next[key] !== value) {
      next[key] = value;
      changed = true;
    }
  });

  return changed ? next : form;
}

/**
 * The body to PUT: the form, minus nothing. Kept as its own function so the "what goes on the wire"
 * decision has one home - if the form ever gains a field the server does not own, it gets filtered
 * HERE rather than being discovered as a 400 by a customer.
 *
 * Round 14: `serverValues` are the targets-page settings the SERVER defines (see
 * serverTargetSettings / seedServerValues below). They ride along only when given - they were seeded
 * only for keys the server both listed and returned, so a server without them never receives one
 * (it would refuse the whole save as unknown_fields).
 */
export function toTargetDataBody(form, serverValues) {
  if (!serverValues || typeof serverValues !== 'object') return { ...form };
  return { ...form, ...serverValues };
}

/*
 * Round 14 (2026-09-28): the targets-page settings the server defines on top of the 50 fields -
 * "9. Pick targets in order (one per session)" and "10. Auto-remove not-found targets from the
 * list" (they were slot settings 126 / 127 until they moved to this page). Everything about them -
 * the id, the wire key, the label, the tooltip - comes from GET /uiapi/target-data/metadata; nothing
 * is written here (memory: v3-must-not-invent-what-v1-defines). Only checkboxes are drawn, because
 * that is all the server defines; any other control is ignored rather than guessed at.
 */

/** GET /uiapi/target-data/metadata -> [{ id, wire, label, tooltip }] (checkboxes with a wire key). Pure. */
export function serverTargetSettings(meta) {
  const list = meta && Array.isArray(meta.settings) ? meta.settings : [];
  return list
    .filter((s) => s && typeof s.wire === 'string' && s.wire
      && (String(s.control || '').toLowerCase() === 'checkbox' || String(s.javaType || '').toLowerCase() === 'boolean'))
    .map((s) => ({
      id: s.id,
      wire: s.wire,
      label: s.label || s.wire,
      numberedLabel: s.numberedLabel || null,
      tooltip: s.tooltip || '',
    }));
}

/**
 * The values of those settings from the GET response, keyed by wire name - ONLY for a setting the
 * metadata lists AND the response carries (the server serves a key only while its part is ON). A
 * null is "unticked" (false), as on the server. Pure.
 */
export function seedServerValues(settings, response) {
  const out = {};
  if (!Array.isArray(settings) || !response || typeof response !== 'object') return out;
  settings.forEach((s) => {
    if (Object.prototype.hasOwnProperty.call(response, s.wire)) {
      out[s.wire] = response[s.wire] === true;
    }
  });
  return out;
}
